import { Box, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { AuthoringBoundEditableRichText } from "authoring/components/authoring-editable-rich-text"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useSave } from "lib/@hooks/use-save"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { AuthoringHeaderBarRight } from "authoring/routes/@layouts/authoring-app-layout"
import { updateProjectGroup } from "../controller/update-project-group"
import { useParams } from "react-router"
import { useDarkMode } from "lib/use-dark-mode"
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import { AmendsBoundaryButton } from "authoring/routes/project/amends/components/amends-boundary-button"
import { AmendsBoundaryEditor } from "authoring/routes/project/amends/components/amends-boundary-editor"
import { AmendsBoundary } from "authoring/routes/project/amends/components/amends-boundary"
import { useGroupAmends } from "../lib/use-group-amends"
import { INTRO_CONTENT_AMENDS_KEY, INTRO_NOTE_AMENDS_KEY } from "../../amends/lib/constants/amends-keys"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { ScheduleMacroAutocomplete } from "authoring/routes/project/schedule-editor/schedule-macro-autocomplete"
import { useChipExpander } from "authoring/routes/macro/hooks/use-chip-expander"
import { IntroductionAmendsHint } from "./introduction-amends-hint"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import { extractMacroTokensFromString } from "authoring/routes/macro/lib/macro-token-utils"
import { buildEditableIntro } from "authoring/routes/macro/lib/build-editable-authoring-value"
import { stripChipHtml } from "authoring/routes/macro/lib/macro-chip-utils"
import { areAllSchedulesNew } from "../lib/are-all-schedules-new.mjs"

const SAVE_ID = "intro-group-editor"

export function IntroductionGroupEditor() {
    const { projectId, groupId } = useParams()
    const { projectGroup, groupLoading, savingRef } = useBoundContext()
    // Notes justify a change against existing published content, so they stay author's-choice
    // when every current target is a schedule that has none yet.
    const notesRequired = !areAllSchedulesNew(projectGroup?.introTypeSchedules)
    useEffect(resetSavingAfterLoad, [groupLoading, savingRef])
    const isDark = useDarkMode()
    const [intro, setIntro] = useState(null)
    const hydratedIntroRef = useRef("")
    const [saveReady, setSaveReady] = useState(false)
    const deferredIntroContent = useDeferredValue(intro?.content)
    const deferredIntroNote = useDeferredValue(intro?.note)
    const tokenIds = useMemo(collectIntroTokenIds, [
        deferredIntroContent,
        deferredIntroNote,
        projectGroup?.intro?.content,
        projectGroup?.intro?.note,
    ])
    const { macros: allMacros, macroMap, loading: macrosLoading } = useEnrichedMacroMap({ tokenIds })
    const { handleChipClick, chipExpanderPopover } = useChipExpander({ macroMap })
    useEffect(syncIntroFromGroup, [intro, projectGroup, groupLoading, macrosLoading, macroMap, saveReady])

    const onChange = useSave({ save: saveIntro, target: intro, id: SAVE_ID, wait: 1000 }, [projectId, groupId])
    const refresh = useRefresh()
    const { amends, saveAmend, deleteAmend } = useGroupAmends({ projectId, groupId })

    if (!intro) return <LoadingScreen />

    return (
        <Stack spacing={2}>
            <SaveIndicator id={SAVE_ID} slot={AuthoringHeaderBarRight} />
            <AmendsBoundary amends={amends} onSaveAmend={saveAmend} onDeleteAmend={deleteAmend}>
                <Box onClickCapture={handleChipClick}>
                    <Bound target={intro} onChange={handleChange} refresh={refresh} editMode={true} macroMap={macroMap}>
                        <IntroductionAmendsHint />
                        <Box>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                data-cy="intro-content-amends-row"
                            >
                                <Typography variant="subtitle2">Introduction</Typography>
                                <AmendsBoundaryButton amendsKey={INTRO_CONTENT_AMENDS_KEY} />
                            </Stack>
                            <AuthoringBoundEditableRichText field="content" />
                            <AmendsBoundaryEditor
                                amendsKey={INTRO_CONTENT_AMENDS_KEY}
                                required={notesRequired}
                                data-cy="intro-content-amends"
                            />
                        </Box>
                        <Box sx={{ mt: 2, pl: 2 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                data-cy="intro-note-amends-row"
                            >
                                <Typography variant="overline">Notes</Typography>
                                <AmendsBoundaryButton amendsKey={INTRO_NOTE_AMENDS_KEY} />
                            </Stack>
                            <Box sx={{ color: isDark ? "#aaa" : "#333" }}>
                                <AuthoringBoundEditableRichText field="note" />
                            </Box>
                            <AmendsBoundaryEditor
                                amendsKey={INTRO_NOTE_AMENDS_KEY}
                                subtitle="Change Note Reason"
                                required={notesRequired}
                                data-cy="intro-note-amends"
                            />
                        </Box>
                    </Bound>
                </Box>
                <ScheduleMacroAutocomplete macros={allMacros} enabled={true} isPreviewMode />
            </AmendsBoundary>
            {chipExpanderPopover}
        </Stack>
    )

    function syncIntroFromGroup() {
        if (groupLoading || macrosLoading || !projectGroup) return

        const nextSource = projectGroup?.intro ?? { content: "", note: "" }
        const nextHydratedKey = buildIntroHydratedKey(nextSource)

        if (!saveReady) {
            setIntro(buildEditableIntro(nextSource, macroMap))
            hydratedIntroRef.current = nextHydratedKey
            setSaveReady(true)
            return
        }

        if (nextHydratedKey === hydratedIntroRef.current) return

        const currentNormalizedKey = buildIntroHydratedKey(intro)
        if (currentNormalizedKey !== hydratedIntroRef.current && currentNormalizedKey !== nextHydratedKey) return

        setIntro(buildEditableIntro(nextSource, macroMap))
        hydratedIntroRef.current = nextHydratedKey
    }

    function handleChange() {
        refresh()
        if (saveReady) onChange()
    }

    function resetSavingAfterLoad() {
        if (!groupLoading && savingRef.current) {
            savingRef.current = false
        }
    }

    async function saveIntro(intro) {
        if (!projectId || !groupId) return true
        savingRef.current = true
        const cleanedIntro = {
            ...intro,
            content: stripChipHtml(intro?.content),
            note: stripChipHtml(intro?.note),
        }
        const saved = await updateProjectGroup({ projectId, group: { id: groupId, intro: cleanedIntro } })
        if (!saved) throw new Error("Failed to save introductions")
        return true
    }

    function collectIntroTokenIds() {
        const tokenIds = new Set()
        for (const tokenId of extractMacroTokensFromString(deferredIntroContent ?? projectGroup?.intro?.content))
            tokenIds.add(tokenId)
        for (const tokenId of extractMacroTokensFromString(deferredIntroNote ?? projectGroup?.intro?.note))
            tokenIds.add(tokenId)
        return [...tokenIds]
    }

    function buildIntroHydratedKey(value = {}) {
        return JSON.stringify({
            content: stripChipHtml(value?.content ?? ""),
            note: stripChipHtml(value?.note ?? ""),
        })
    }
}
